import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { humanize } from 'underscore.string';
import { uniq } from 'lodash';
import { CSV_FILE_TYPES } from 'config/accepted-file-types';
import { carrierEdiEvents } from 'components/modules/shipments/mappings';
import { FileUploader } from 'components/v2/FileUploader';
import Checkbox from 'components/v2/forms/Checkbox';
import CargomaticRadio from 'components/v2/forms/Radio';
import CargomaticInput from 'components/v2/input';
import Alert from 'components/ui/Alert';
import LoadingCircle from 'components/ui/LoadingCircle';
import { showToast } from 'stores/ToastStore';
import { convertCommaSeparatedStringToArray } from 'helpers/strings';
import { sluggify } from 'utils/localization/utils';
import { runPrecheck, resendCarrierEdiEvents, resendEDICarrierEventsBulk, } from 'utils/ShipmentWebAPIUtils';
import CustomerStatus from './CustomerStatus';
import ReferenceNumberTable from './ReferenceNumberTable';
const ASSIGNED_CARRIERS = 'assigned_carriers';
const CARRIES_WITH_OFFERS = 'carriers_with_offers';
export default function CarrierEdiEvents() {
    const { t } = useTranslation();
    const [checkedEventsMap, setCheckedEventsMap] = useState({});
    const [results, setResults] = useState([]);
    const [runningPrecheck, setRunningPrecheck] = useState(false);
    const [resendingEvents, setResendingEvents] = useState(false);
    const [carrierStatusList, setCarrierStatusList] = useState([]);
    const [text, setText] = useState('');
    const [targetCarriers, setTargetCarriers] = useState(ASSIGNED_CARRIERS);
    const [referenceNumbers, setReferenceNumbers] = useState([]);
    const [errors, setErrors] = useState({});
    const [ranPrecheck, setRanPrecheck] = useState(false);
    const [resendEDIError, setResendEDIError] = useState(false);
    const [resendEDISuccess, setResendEDISuccess] = useState(false);
    const [resendEDISuccessMessage, setResendEDISuccessMessage] = useState('');
    const [showRunPrecheckBanner, setShowRunPrecheckBanner] = useState(true);
    const [uploadPending, setUploadPending] = useState(false);
    const [files, setFiles] = useState([]);
    const fileUploaderRef = useRef(null);
    useEffect(() => {
        document.title = `${t('RESEND_EDI')} - ${t('CARRIER_EVENTS')}`;
    }, []);
    function addFiles(files) {
        // in case there's anything in the textarea beforehand
        setText('');
        if (files) {
            setFiles(files);
        }
    }
    const clearFiles = () => {
        fileUploaderRef.current.clearFiles();
        setFiles([]);
    };
    function isFormEmpty() {
        return (Object.keys(checkedEventsMap).length === 0 ||
            (referenceNumbers.length === 0 && files.length === 0));
    }
    function isRunPrecheckDisabled() {
        return isFormEmpty() || runningPrecheck;
    }
    function isResendDisabled() {
        return isFormEmpty() || resendingEvents;
    }
    function handleTargetCarriersChange(value) {
        setTargetCarriers(value);
        setRanPrecheck(false);
    }
    function handleHideEDIResendAlert() {
        setResendEDISuccess(false);
        setResendEDIError(false);
    }
    function handleHideRunPrecheckAlert() {
        setShowRunPrecheckBanner(false);
    }
    function handleReset() {
        setText('');
        setReferenceNumbers([]);
        setRanPrecheck(false);
        setCheckedEventsMap({});
        clearFiles();
        setCarrierStatusList([]);
        setResults([]);
        setResendEDISuccess(false);
        setResendEDIError(false);
        setShowRunPrecheckBanner(true);
    }
    function handleToggleEvent(event) {
        return function (checked) {
            setRanPrecheck(false);
            setCheckedEventsMap((map) => {
                const updated = Object.assign({}, map);
                if (checked) {
                    updated[event.slug] = true;
                }
                else {
                    delete updated[event.slug];
                }
                return updated;
            });
        };
    }
    function handleTextChange(updatedText) {
        setText(updatedText);
    }
    async function handleParseReferenceNumbers() {
        const newReferenceNumbers = convertCommaSeparatedStringToArray(text);
        setReferenceNumbers((current) => uniq([...current, ...newReferenceNumbers]));
        setText('');
        setRanPrecheck(false);
    }
    function processPayload() {
        const events = carrierEdiEvents
            .filter((ediEvent) => checkedEventsMap[ediEvent.slug])
            .map((ediEvent) => ediEvent.slug);
        const validationErrors = {};
        if (events.length === 0) {
            validationErrors.events = t('SELECT_ONE_EVENT');
        }
        if (referenceNumbers.length === 0 && files.length === 0) {
            validationErrors.referenceNumbers = t('PROVIDE_ATLEAST_ONE_REF_NUM');
        }
        setErrors(validationErrors);
        return { events, validationErrors };
    }
    async function handleRunPrecheck() {
        const { events, validationErrors } = processPayload();
        if (Object.keys(validationErrors).length > 0) {
            return;
        }
        try {
            setRanPrecheck(false);
            setRunningPrecheck(true);
            setResults([]);
            const result = await runPrecheck({
                userType: 'carrier',
                referenceNumbers,
                events,
                targetCarriers,
            });
            setCarrierStatusList(result.entity.reportData);
            setRanPrecheck(true);
            setShowRunPrecheckBanner(true);
        }
        catch (e) {
            console.error(e);
        }
        finally {
            setRunningPrecheck(false);
            setText('');
        }
    }
    async function handleUpload() {
        var _a, _b;
        const { events, validationErrors } = processPayload();
        if (files.length !== 1) {
            validationErrors.invalid_file_amount = t('ATTACH_VALID_CSV_FILE');
        }
        if (Object.keys(validationErrors).length > 0) {
            setErrors(validationErrors);
            return;
        }
        setUploadPending(true);
        const [file] = files;
        console.log({ file, events, validationErrors });
        try {
            await resendEDICarrierEventsBulk({
                file,
                events,
                targetCarriers,
            });
            showToast(t('CSV_UPLOAD_SUCCESSFUL'), t('DATA_UPLOADED_WAIT_FOR_EMAIL'));
            setResendEDISuccess(true);
            setResendEDISuccessMessage(t('DATA_UPLOADED_WAIT_FOR_EMAIL'));
        }
        catch (e) {
            const errorMsg = ((_b = (_a = e === null || e === void 0 ? void 0 : e.response) === null || _a === void 0 ? void 0 : _a.body) === null || _b === void 0 ? void 0 : _b.message) || 'SOMETHING_WENT_WRONG';
            showToast(t('CSV_UPLOAD_FAILED'), t(sluggify(errorMsg), 'error'));
            setResendEDIError(t(sluggify(errorMsg)));
        }
        finally {
            setUploadPending(false);
        }
    }
    async function handleResendEvents() {
        const { events, validationErrors } = processPayload();
        if (Object.keys(validationErrors).length > 0) {
            return;
        }
        try {
            setResendingEvents(true);
            setResendEDIError(false);
            setResendEDISuccess(false);
            const result = await resendCarrierEdiEvents({
                reference_numbers: referenceNumbers,
                events,
                targetCarriers,
            });
            setResults(result.entity.reportData);
            setCarrierStatusList([]);
            setResendEDISuccess(true);
        }
        catch (e) {
            console.error(e);
            setResendEDIError(true);
        }
        finally {
            setText('');
            setResendingEvents(false);
        }
    }
    function handleRemove(referenceNumber) {
        setReferenceNumbers(referenceNumbers.filter((refNum) => refNum !== referenceNumber));
        setRanPrecheck(false);
    }
    function renderEvents() {
        return (React.createElement("div", null,
            React.createElement("label", { className: "mb-3 form-label" }, t('MILESTONES_LABEL')),
            React.createElement("div", { className: "grid grid-cols-2 sm:grid-cols-4 gap-4 mb-4" }, carrierEdiEvents.map((ediEvent) => {
                return (React.createElement("div", { key: ediEvent.slug },
                    React.createElement(Checkbox, { id: ediEvent.slug, name: ediEvent.slug, checked: checkedEventsMap[ediEvent.slug], onChange: handleToggleEvent(ediEvent) }, t(ediEvent.localizationSlug))));
            }))));
    }
    function renderAlerts() {
        let type;
        let message;
        if (resendEDISuccess) {
            type = 'success';
            message = resendEDISuccessMessage || t('RESEND_EDI_SUCCESS');
        }
        else if (resendEDIError) {
            type = 'error';
            message = t('RESEND_EDI_ERROR');
        }
        return (React.createElement("div", null, message && (React.createElement(Alert, { className: "mb-4", type: type, onRemove: handleHideEDIResendAlert }, message))));
    }
    function renderCarrierRows() {
        if (carrierStatusList.length === 0) {
            return null;
        }
        return (React.createElement("div", { className: "" },
            showRunPrecheckBanner && (React.createElement(Alert, { type: "info", className: "mb-4", onRemove: handleHideRunPrecheckAlert }, t('PRECHECK_MESSAGE'))),
            React.createElement(CustomerStatus, { userType: "carrier", rows: carrierStatusList })));
    }
    function renderResults() {
        if (results.length === 0) {
            return null;
        }
        return (React.createElement("div", { className: "" },
            renderAlerts(),
            React.createElement(CustomerStatus, { userType: "carrier", rows: results })));
    }
    function renderReferenceNumbers() {
        if (referenceNumbers.length === 0) {
            return null;
        }
        const label = t('REFERENCE_NUMBER');
        return (React.createElement("div", { className: "mt-6" },
            React.createElement(ReferenceNumberTable, { referenceNumbers: referenceNumbers, onRemove: handleRemove, label: label })));
    }
    function renderErrors() {
        if (Object.keys(errors).length === 0) {
            return null;
        }
        return (React.createElement(Alert, { type: "error", className: "my-4" },
            React.createElement("ul", null, Object.values(errors).map((error) => (React.createElement("li", { key: error }, error))))));
    }
    return (React.createElement("div", { className: "" },
        React.createElement("div", { className: "" },
            React.createElement("div", { className: "bg-white shadow rounded-md" },
                React.createElement("div", { className: "px-4 py-5 sm:p-6" },
                    renderEvents(),
                    React.createElement("div", { className: "my-4" },
                        React.createElement("label", { className: "form-label mb-1 block" }, t('TARGET_CARRIERS_LABEL')),
                        React.createElement("div", { className: "flex flex-wrap -mx-2" },
                            React.createElement("div", { className: "px-2" },
                                React.createElement(CargomaticRadio, { id: ASSIGNED_CARRIERS, name: "targetCarriers", className: "mb-2", value: ASSIGNED_CARRIERS, checked: targetCarriers === ASSIGNED_CARRIERS, onChange: handleTargetCarriersChange }, t('ASSIGNED_CARRIERS'))),
                            React.createElement("div", { className: "px-2" },
                                React.createElement(CargomaticRadio, { id: CARRIES_WITH_OFFERS, name: "targetCarriers", className: "mb-2", value: CARRIES_WITH_OFFERS, checked: targetCarriers === CARRIES_WITH_OFFERS, onChange: handleTargetCarriersChange }, t('CARRIERS_WITH_OFFERS'))))),
                    React.createElement("hr", null),
                    React.createElement("label", { className: "mb-3 form-label" },
                        t('PROVIDE'),
                        " ",
                        t('CSV')),
                    React.createElement(FileUploader, { fileTypes: CSV_FILE_TYPES, fileTypesLabel: t('CSV'), onChange: addFiles, ref: fileUploaderRef }),
                    React.createElement("div", { className: "separator mt-4 mb-4" },
                        React.createElement("div", { className: "separator-text form-label" }, t('OR').toUpperCase())),
                    React.createElement(CargomaticInput, { type: "textarea", className: "mt-2", label: t('REFERENCE_NUMBERS_LABEL'), onChange: handleTextChange, placeholder: t('COMMA_OR_LINE_SEPARATED_LIST'), value: text }),
                    React.createElement("div", { className: "mt-2 flex justify-end" },
                        React.createElement("button", { className: "button button-hollow-green", disabled: !text, onClick: handleParseReferenceNumbers }, t('ADD'))),
                    renderReferenceNumbers()),
                React.createElement("div", { className: "p-4 flex justify-end mt-6 space-x-2 bg-gray-100" }, files.length > 0 ? (React.createElement("div", { className: "" },
                    React.createElement("div", { className: "" }, !uploadPending && renderAlerts()),
                    React.createElement("div", { className: "p-4 flex justify-end space-x-2 bg-gray-100" },
                        React.createElement("button", { className: "button button-secondary", onClick: handleReset }, t('RESET')),
                        React.createElement("button", { className: "button button-green", disabled: isResendDisabled(), onClick: handleUpload },
                            uploadPending && (React.createElement(LoadingCircle, { text: t('UPLOADING') + '...' })),
                            !uploadPending && (React.createElement("span", null,
                                t('UPLOAD'),
                                " ",
                                t('CSV'))))))) : (React.createElement("button", { className: "button button-green", disabled: isRunPrecheckDisabled(), onClick: handleRunPrecheck },
                    runningPrecheck && (React.createElement(LoadingCircle, { text: t('CHECKING') + '...' })),
                    !runningPrecheck && React.createElement("span", null, t('CONTINUE'))))),
                renderErrors())),
        ranPrecheck && (React.createElement("div", { className: "" },
            React.createElement("div", { className: "" }, renderCarrierRows()),
            React.createElement("div", { className: "" }, renderResults()),
            React.createElement("div", { className: "p-4 flex justify-end space-x-2 bg-gray-100" },
                React.createElement("button", { className: "button button-secondary", onClick: handleReset }, t('RESET')),
                React.createElement("button", { className: "button button-green", disabled: isResendDisabled(), onClick: handleResendEvents },
                    resendingEvents && (React.createElement(LoadingCircle, { text: t('SENDING_EVENTS') + '...' })),
                    !resendingEvents && React.createElement("span", null, t('RESEND'))))))));
}
